import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import posthog from "posthog-js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as UIButton from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Users,
  Wifi,
  Coffee,
  Sofa,
  ArrowRight,
  Map,
  Grid3x3,
  Calendar,
  Heart,
  SearchX,
  ArrowUpDown,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { getDisplayMonthlyRentEURFromBookingAndPayments } from "@/lib/rent";
import { resolvePublicStreet } from "@/lib/address";
import { normalizeImageUrl, toImageArray } from "@/lib/roomImages";
import {
  getListingAvailability,
  moveInAllowsBooking,
  parseDateOnly,
} from "@/lib/roomAvailability";
import PropertyMapView from "@/components/PropertyMapView";
import { AuthModal } from "@/components/auth/AuthModal";
import { type PropertyRoom } from "@/components/properties";
import {
  SearchFilterBar,
  ResultsHeader,
  RoomResultCard,
  WaitlistBand,
  TenantFaq,
  NotifyBand,
} from "@/components/properties/RoomSearchSections";
import {
  INK,
  MUTED,
  MUTED_SOFT,
  SAND,
  LINE,
  EASE,
} from "@/lib/marketing-theme";
import {
  isPublicRoom,
  publishedCities,
  type PublicRoomRow,
} from "@/lib/listingVisibility";
import { toast } from "sonner";
import room1 from "@/assets/room-1.jpg";

const LIKED_ROOMS_KEY = "property-detail-liked-rooms";
import room2 from "@/assets/room-2.jpg";
import room3 from "@/assets/room-3.jpg";
import heroProperties from "@/assets/room-4.webp";

const ROOMS_SELECT_PROPERTIES = `
  id,
  title,
  city,
  address,
  street_name,
  lat,
  lng,
  description,
  image_url,
  is_hidden
`;

/** Logged-in: same booking + rent rows as property detail so grid cards match /property/:id. */
function userListingRentBoostFromRoomRow(
  room: { bookings?: unknown },
  userId: string | undefined,
  moveInDateStr: string
): number {
  if (!userId) return 0;
  const raw = room.bookings;
  if (!raw || !Array.isArray(raw)) return 0;
  const filterYm = moveInDateStr.trim().slice(0, 7) || null;
  let boost = 0;
  for (const b of raw) {
    if (!b || typeof b !== "object") continue;
    const row = b as {
      user_id?: string | null;
      monthly_rent?: number | null;
      start_date?: string | null;
      move_in_date?: string | null;
      payments?: {
        amount_cents?: number | null;
        gross_amount_cents?: number | null;
        due_date?: string | null;
        type?: string | null;
      }[];
    };
    if (String(row.user_id ?? "") !== String(userId)) continue;
    const rentPayments = (row.payments || []).filter((p) => p?.type === "rent");
    const stub = { monthly_rent: row.monthly_rent, rooms: { price: 0 } };
    const preferYm =
      filterYm ||
      String(row.move_in_date || row.start_date || "")
        .trim()
        .slice(0, 7) ||
      null;
    const v = getDisplayMonthlyRentEURFromBookingAndPayments(stub, {
      rentPayments,
      preferCalendarMonthYm: preferYm,
    });
    if (v > boost) boost = v;
  }
  return boost;
}

// Fallback mock data (for development/testing) - defined outside component
const mockProperties = [
  {
    id: 1,
    roomId: 1,
    propertyId: 1,
    title: "Bright Studio in Mitte",
    buildingName: "Property",
    city: "Berlin",
    address: "",
    price: 850,
    image: room1,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 3,
    available: true,
    lat: 52.52,
    lng: 13.405,
    description: "",
    propertyDescription: "",
    roomImages: [room1],
    propertyImage: null,
  },
  {
    id: 2,
    roomId: 2,
    propertyId: 2,
    title: "Cozy Room in Kreuzberg",
    buildingName: "Property",
    city: "Berlin",
    address: "",
    price: 750,
    image: room2,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 4,
    available: true,
    lat: 52.4987,
    lng: 13.4094,
    description: "",
    propertyDescription: "",
    roomImages: [room2],
    propertyImage: null,
  },
  {
    id: 3,
    roomId: 3,
    propertyId: 3,
    title: "Modern Loft in Schwabing",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 920,
    image: room3,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 3,
    available: true,
    lat: 48.1549,
    lng: 11.5892,
    description: "",
    propertyDescription: "",
    roomImages: [room3],
    propertyImage: null,
  },
  {
    id: 4,
    roomId: 4,
    propertyId: 4,
    title: "Spacious Room in Maxvorstadt",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 880,
    image: room1,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 4,
    available: true,
    lat: 48.15,
    lng: 11.5667,
    description: "",
    propertyDescription: "",
    roomImages: [room1],
    propertyImage: null,
  },
  {
    id: 5,
    roomId: 5,
    propertyId: 5,
    title: "Designer Studio in Haidhausen",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 950,
    image: room2,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 2,
    available: true,
    lat: 48.132,
    lng: 11.604,
    description: "",
    propertyDescription: "",
    roomImages: [room2],
    propertyImage: null,
  },
  {
    id: 6,
    roomId: 6,
    propertyId: 6,
    title: "Sunny Room in Sendling",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 820,
    image: room3,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 5,
    available: true,
    lat: 48.11,
    lng: 11.54,
    description: "",
    propertyDescription: "",
    roomImages: [room3],
    propertyImage: null,
  },
  {
    id: 7,
    roomId: 7,
    propertyId: 7,
    title: "Premium Loft in Lehel",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 1050,
    image: room1,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 3,
    available: false,
    lat: 48.143,
    lng: 11.592,
    description: "",
    propertyDescription: "",
    roomImages: [room1],
    propertyImage: null,
  },
  {
    id: 8,
    roomId: 8,
    propertyId: 8,
    title: "Charming Room in Prenzlauer Berg",
    buildingName: "Property",
    city: "Berlin",
    address: "",
    price: 800,
    image: room2,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 4,
    available: true,
    lat: 52.531,
    lng: 13.4197,
    description: "",
    propertyDescription: "",
    roomImages: [room2],
    propertyImage: null,
  },
  {
    id: 9,
    roomId: 9,
    propertyId: 9,
    title: "Modern Studio in Neuhausen",
    buildingName: "Property",
    city: "Munich",
    address: "",
    price: 890,
    image: room3,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 3,
    available: true,
    lat: 48.158,
    lng: 11.532,
    description: "",
    propertyDescription: "",
    roomImages: [room3],
    propertyImage: null,
  },
  {
    id: 10,
    roomId: 10,
    propertyId: 10,
    title: "Lake View Room in Tutzing",
    buildingName: "Property",
    city: "Tutzing",
    address: "",
    price: 780,
    image: room1,
    amenities: [
      "Wifi available",
      "Shared Kitchen",
      "Shared Bathroom",
      "Shared WC",
      "Shared Laundry",
      "Private desk",
      "Fully furnished",
    ],
    roommates: 2,
    available: true,
    lat: 47.9094,
    lng: 11.2803,
    description: "",
    propertyDescription: "",
    roomImages: [room1],
    propertyImage: null,
  },
];

/** Mock rooms in listing order (available first). Static, so built once. */
const SORTED_MOCK_PROPERTIES = mockProperties
  .map((p) => ({ ...p, nextAvailableDate: null as string | null }))
  .sort((a, b) => {
    if (a.available && !b.available) return -1;
    if (!a.available && b.available) return 1;
    return 0;
  });

const Properties = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { location: locationParam } = useParams<{ location?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const [selectedCity, setSelectedCity] = useState("all");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [moveInDate, setMoveInDate] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);

  // Seed filters from the home-page search bar (query params), once on mount.
  // City comes through as an exact value (e.g. "München") so it matches the
  // room list directly without waiting on the city list to load.
  useEffect(() => {
    const city = searchParams.get("city");
    const budget = searchParams.get("budget");
    const movein = searchParams.get("movein");
    if (city) setSelectedCity(city);
    if (budget) setPriceRange((p) => ({ ...p, max: budget }));
    if (movein) setMoveInDate(movein);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isFirstFilterRender = useRef(true);
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [sortBy, setSortBy] = useState<
    "recommended" | "price-asc" | "price-desc" | "newest"
  >("recommended");
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [guestLikedRoomIds, setGuestLikedRoomIds] = useState<Set<number>>(
    () => {
      if (typeof window === "undefined") return new Set();
      try {
        const raw = localStorage.getItem(LIKED_ROOMS_KEY);
        const arr = raw ? (JSON.parse(raw) as string[]) : [];
        return new Set(arr.map((id) => Number(id)).filter(Boolean));
      } catch {
        return new Set();
      }
    }
  );

  // Sync guest likes from localStorage when returning from property detail (e.g. liked there)
  useEffect(() => {
    if (user) return;
    const sync = () => {
      try {
        const raw = localStorage.getItem(LIKED_ROOMS_KEY);
        const arr = raw ? (JSON.parse(raw) as string[]) : [];
        setGuestLikedRoomIds(
          new Set(arr.map((id) => Number(id)).filter(Boolean))
        );
      } catch {
        /* ignore localStorage / JSON errors */
      }
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, [user]);

  // Always show properties from the top when opening the page (e.g. from "View Rooms")
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user's favorites (only if logged in)
  const { data: favoritesData } = useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("favorites")
        .select("room_id")
        .eq("user_id", user.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Get set of favorited room IDs: from Supabase when logged in, from localStorage when guest
  const favoritedRoomIds = useMemo(() => {
    if (user && favoritesData)
      return new Set(favoritesData.map((f: { room_id: number }) => f.room_id));
    if (!user) return guestLikedRoomIds;
    return new Set<number>();
  }, [user, favoritesData, guestLikedRoomIds]);

  // Toggle favorite mutation (logged-in users) or local state (guests)
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({
      propertyId,
      roomId,
      isFavorited,
    }: {
      propertyId: number;
      roomId: number;
      isFavorited: boolean;
    }) => {
      if (user?.id) {
        if (isFavorited) {
          const { error } = await supabase
            .from("favorites")
            .delete()
            .eq("user_id", user.id)
            .eq("room_id", roomId);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("favorites")
            .insert([{ user_id: user.id, room_id: roomId }]);
          if (error) throw error;
        }
        return;
      }
      // Guest: update localStorage (same key as PropertyDetail) so detail page stays in sync
      try {
        const raw = localStorage.getItem(LIKED_ROOMS_KEY);
        const set = raw
          ? new Set(JSON.parse(raw) as string[])
          : new Set<string>();
        if (isFavorited) set.delete(String(roomId));
        else set.add(String(roomId));
        localStorage.setItem(LIKED_ROOMS_KEY, JSON.stringify([...set]));
        setGuestLikedRoomIds(
          new Set([...set].map((id) => Number(id)).filter(Boolean))
        );
      } catch {
        /* ignore localStorage / JSON errors */
      }
    },
    onSuccess: (_, variables) => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
        toast.success(
          variables.isFavorited
            ? t("properties.favoriteRemoved") || "Removed from favorites"
            : t("properties.favoriteAdded") || "Added to favorites"
        );
      }
      posthog.capture(
        variables.isFavorited ? "room unfavorited" : "room favorited",
        {
          room_id: variables.roomId,
          property_id: variables.propertyId,
        }
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update favorites");
    },
  });

  // Fetch ROOMS (embed this user's bookings + rent payments when logged in — matches PropertyDetail pricing on cards)
  const {
    data: roomsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["rooms", user?.id ?? "anon"],
    queryFn: async () => {
      const select = user?.id
        ? `
          *,
          properties (
            ${ROOMS_SELECT_PROPERTIES}
          ),
          bookings (
            id,
            monthly_rent,
            start_date,
            move_in_date,
            user_id,
            payments (
              amount_cents,
              gross_amount_cents,
              due_date,
              type
            )
          )
        `
        : `
          *,
          properties (
            ${ROOMS_SELECT_PROPERTIES}
          )
        `;
      const { data: rooms, error: roomsError } = await supabase
        .from("rooms")
        .select(select)
        .order("created_at", { ascending: false });

      if (roomsError) throw roomsError;
      return rooms || [];
    },
    staleTime: 0, // Always consider data stale, refetch on mount
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Rooms the public may see at all: not hidden, and a real room rather than a
  // CMS image label. Availability is applied separately below, so the mock
  // fallback can tell "no rooms published" from "all rooms occupied".
  const publicRooms = useMemo(() => {
    if (!roomsData || !Array.isArray(roomsData)) return [] as PublicRoomRow[];
    return (roomsData as unknown as PublicRoomRow[]).filter(isPublicRoom);
  }, [roomsData]);

  // Transform rooms data to match expected format
  const rooms = useMemo(() => {
    // Every published room is listed, occupied ones included: hiding them left
    // "2 rooms found" on a portfolio of 36 rooms, so a visitor looking for
    // next summer saw an almost empty site. A taken room reads "Unavailable"
    // and stays un-bookable until a move-out is confirmed in the move-out
    // protocol; only then does its free-from date appear on the card.
    const transformed = publicRooms.map(
      (room: {
        id: number;
        title: string;
        price: number;
        available: boolean;
        available_from?: string | null;
        next_available_date?: string | null;
        bookings?: unknown;
        description?: string;
        images?: unknown;
        properties?: {
          id: number;
          title?: string;
          city?: string;
          address?: string;
          lat?: number;
          lng?: number;
          description?: string;
          image_url?: string | null;
          is_hidden?: boolean;
        } | null;
      }) => {
        const property = room.properties;

        // Same rule the detail page uses: a room with a future availability
        // date is not bookable yet, and one whose date has arrived is bookable
        // again regardless of the raw `available` flag. The date comes from the
        // admin-owned `available_from` (see lib/roomAvailability).
        const availabilityInput = {
          available: room.available,
          available_from: room.available_from,
          next_available_date: room.next_available_date,
        };
        // A taken room only gets a date once a move-out is confirmed; without
        // one the card says "Unavailable".
        const { listingAvailable, nextAvailableDate } =
          getListingAvailability(availabilityInput);

        // Parse room images — JSONB arrives as a string, array or object.
        const roomImages = toImageArray(room.images)
          .map(normalizeImageUrl)
          .filter((img): img is string => img !== null);

        // Get property image URL
        const propertyImageUrl = property?.image_url
          ? normalizeImageUrl(property.image_url)
          : null;

        // Determine final image URL - prefer room images, fallback to property image, then default
        let imageUrl = room1; // Default fallback

        if (roomImages.length > 0) {
          imageUrl = roomImages[0];
        } else if (propertyImageUrl) {
          imageUrl = propertyImageUrl;
        }

        return {
          id: room.id, // Room ID (for detail page)
          roomId: room.id, // Keep room ID reference
          propertyId: property?.id, // Property ID for context
          title: room.title, // Room title
          buildingName: property?.title || "Property", // Building/property name
          city: property?.city || "",
          // COL-218: redact house number on public listings. Derive from the
          // admin-maintained `address` column; the legacy contract street_name
          // is only a fallback (see resolvePublicStreet).
          address: resolvePublicStreet(property),
          price: Math.max(
            Number(room.price) || 0,
            userListingRentBoostFromRoomRow(room, user?.id, moveInDate)
          ),
          image: imageUrl,
          amenities: [
            "Wifi available",
            "Shared Kitchen",
            "Shared Bathroom",
            "Shared WC",
            "Shared Laundry",
            "Private desk",
            "Fully furnished",
          ], // Default amenities
          available: listingAvailable,
          nextAvailableDate,
          lat: property?.lat || 0,
          lng: property?.lng || 0,
          description: room.description || property?.description || "",
          propertyDescription: property?.description || "",
          roomImages: roomImages,
          propertyImage: propertyImageUrl,
        };
      }
    );

    // Sort: available rooms first, then the ones with a confirmed free-from
    // date, soonest first — a visitor scanning the grid should reach a room
    // they can move into before one that is simply unavailable.
    const freesUpOn = (r: { nextAvailableDate?: string | null }) =>
      r.nextAvailableDate ?? "9999-12-31";
    return transformed.sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      return freesUpOn(a).localeCompare(freesUpOn(b));
    });
  }, [publicRooms, user?.id, moveInDate]);

  // Use fetched rooms, fallback to mock if empty. Mocks stand in for "nothing
  // published yet" only — never for a real portfolio, however it is let.
  const displayRooms = useMemo(
    () => (rooms.length > 0 ? rooms : SORTED_MOCK_PROPERTIES),
    [rooms]
  );

  // City chips describe whatever the grid is showing. Memoised: this feeds the
  // URL-sync effect below, which must not re-run on every render.
  const cities = useMemo(
    () => ["all", ...new Set(displayRooms.map((r) => r.city).filter(Boolean))],
    [displayRooms]
  );

  // Cities of the REAL (published, non-hidden) listings — the only list a
  // /rooms/<city> URL may be judged against. Deliberately built from
  // `publicRooms`, not the availability-filtered `rooms`: a city can have
  // published rooms that are all temporarily non-listable (occupied, no
  // confirmed move-out date) while another city has a listable one, and
  // that city's slug must still resolve to its own (correctly empty) grid
  // rather than bounce to the unfiltered list. Validating against `cities`
  // (the pre-existing bug) meant that while the rooms query was in flight
  // the mock fallback's invented cities (Berlin, Munich, Tutzing) decided
  // the outcome, so a shared link like /rooms/augsburg was redirected
  // to the unfiltered list before the real data ever arrived.
  const realCities = useMemo(() => publishedCities(publicRooms), [publicRooms]);

  const cityParam = searchParams.get("city");

  // Sync URL location param to selected city (e.g. /rooms/munich -> Munich)
  useEffect(() => {
    if (!locationParam) {
      // Don't clobber a city that came in via ?city= from the home search.
      if (!cityParam) setSelectedCity("all");
      return;
    }
    const slug = locationParam.toLowerCase();
    const city = realCities.find((c) => c.toLowerCase() === slug);
    if (city) {
      setSelectedCity(city);
      return;
    }
    // Reject the slug only once real rooms are in; while they load (or when
    // nothing is published) keep the URL and wait rather than redirecting.
    if (isLoading || publicRooms.length === 0) return;
    setSelectedCity("all");
    navigate("/rooms", { replace: true });
  }, [
    locationParam,
    realCities,
    publicRooms.length,
    isLoading,
    navigate,
    cityParam,
  ]);

  const handleCityChange = useCallback(
    (city: string) => {
      setSelectedCity(city);
      if (city === "all") {
        navigate("/rooms", { replace: true });
      } else {
        navigate(`/rooms/${city.toLowerCase()}`, { replace: true });
      }
    },
    [navigate]
  );

  /**
   * Stable identity for an amenity, independent of language and of how the
   * string was typed. The filter and the option list both key on this; only
   * the label is translated.
   */
  const amenityKey = useCallback((a: string) => a.trim().toLowerCase(), []);

  // Declared above the filter pipeline: `filteredRooms` calls it during
  // render to match the amenity multi-select, and a `const` declared
  // further down would still be in its temporal dead zone there.
  const getAmenityDisplayText = useCallback(
    (amenity: string) => {
      const lower = amenity.toLowerCase();
      const withFallback = (
        key: string,
        fallbackEn: string,
        fallbackDe: string
      ) => {
        const translated = t(key)?.trim();
        // `t` echoes the key back when the string is missing from the DB, and
        // a key is truthy — so "empty or the key itself" is what counts as
        // untranslated here. Without the second check the raw
        // "property.amenity.fullyFurnished" reached the cards and the filter.
        const missing = !translated || translated === key;
        return missing
          ? language === "de"
            ? fallbackDe
            : fallbackEn
          : translated;
      };

      if (
        lower.includes("wifi") ||
        lower.includes("wlan") ||
        lower.includes("high-speed") ||
        lower.includes("high speed") ||
        (lower.includes("internet") &&
          (lower.includes("speed") ||
            lower.includes("wi-fi") ||
            lower.includes("wifi") ||
            lower.includes("wlan")))
      ) {
        return language === "de" ? "WLAN verfügbar" : "Wifi available";
      }
      if (lower.includes("kitchen"))
        return language === "de" ? "Gemeinschaftsküche" : "Shared Kitchen";
      if (lower.includes("bathroom"))
        return language === "de" ? "Gemeinschaftsbad" : "Shared Bathroom";
      if (lower === "wc" || lower === "shared wc")
        return language === "de" ? "Gemeinschafts-WC" : "Shared WC";
      if (lower.includes("laundry"))
        return language === "de"
          ? "Gemeinschaftliche Waschküche"
          : "Shared Laundry";
      if (lower.includes("private desk"))
        return withFallback(
          "property.amenity.privateDesk",
          "Private desk",
          "Eigener Schreibtisch"
        );
      if (lower.includes("fully furnished") || lower.includes("furnished"))
        return withFallback(
          "property.amenity.fullyFurnished",
          "Fully furnished",
          "Voll möbliert"
        );
      return amenity;
    },
    [t, language]
  );

  const filteredRooms = useMemo(() => {
    const filtered = displayRooms.filter((room) => {
      // City filter
      if (selectedCity !== "all" && room.city !== selectedCity) {
        return false;
      }

      // Price filter
      const minPrice = priceRange.min ? Number(priceRange.min) : 0;
      const maxPrice = priceRange.max ? Number(priceRange.max) : Infinity;
      if (room.price < minPrice || room.price > maxPrice) {
        return false;
      }

      // Move-in date filter: keep rooms that are free by the requested date —
      // a room that frees up on/before that day still matches.
      if (
        moveInDate &&
        !moveInAllowsBooking(
          moveInDate,
          room.available,
          room.nextAvailableDate ?? null
        )
      ) {
        return false;
      }

      // Available now only toggle
      if (availableOnly && !room.available) {
        return false;
      }

      // Amenities: every picked one has to be present (narrowing, not widening
      // — picking "eigenes Bad" and "Balkon" means both, which is what the
      // multi-select reads as).
      if (selectedAmenities.length) {
        const have = new Set(room.amenities.map(amenityKey));
        if (!selectedAmenities.every((k) => have.has(k))) return false;
      }

      return true;
    });

    return filtered.sort((a, b) => {
      // Unavailable rooms always sink to the bottom, whatever the sort.
      if (a.available !== b.available) {
        return a.available ? -1 : 1;
      }
      switch (sortBy) {
        case "price-asc":
          return a.price - b.price;
        case "price-desc":
          return b.price - a.price;
        case "newest":
          // Higher room id ≈ more recently added.
          return b.roomId - a.roomId;
        case "recommended":
        default:
          return 0; // Keep the fetch order (available-first already applied).
      }
    });
  }, [
    selectedCity,
    priceRange,
    moveInDate,
    availableOnly,
    selectedAmenities,
    getAmenityDisplayText,
    displayRooms,
    sortBy,
  ]);

  // Reset the "load more" window whenever the result set changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [
    selectedCity,
    priceRange,
    moveInDate,
    availableOnly,
    selectedAmenities,
    sortBy,
  ]);

  const visibleRooms = useMemo(
    () => filteredRooms.slice(0, visibleCount),
    [filteredRooms, visibleCount]
  );

  /** Scroll target for the filter bar's Search button. */
  const resultsRef = useRef<HTMLElement>(null);

  /** How many rooms each city currently has, for the picker's right column. */
  const cityCounts = useMemo(() => {
    const counts: Record<string, number> = { all: displayRooms.length };
    for (const room of displayRooms) {
      if (!room.city) continue;
      counts[room.city] = (counts[room.city] ?? 0) + 1;
    }
    return counts;
  }, [displayRooms]);

  /**
   * Slider bounds from the real listings, rounded out to the nearest 50 so the
   * handles sit on tidy numbers. Falls back to a sane span while the rooms
   * are still loading, and always keeps a non-zero range (a portfolio where
   * every room costs the same would otherwise give the slider min === max).
   */
  const priceBounds = useMemo((): [number, number] => {
    const prices = displayRooms.map((r) => r.price).filter((p) => p > 0);
    if (!prices.length) return [300, 1500];
    const lo = Math.floor(Math.min(...prices) / 50) * 50;
    const hi = Math.ceil(Math.max(...prices) / 50) * 50;
    return hi > lo ? [lo, hi] : [lo, lo + 100];
  }, [displayRooms]);

  /**
   * key -> label for every amenity the page has loaded, not just the ones
   * surviving the current filters. The options list narrows as you filter;
   * the summary line still has to name what you picked, so it reads from
   * here instead. Without it a selection whose option dropped out printed
   * its raw key ("shared kitchen") in place of its label.
   */
  const amenityLabels = useMemo(() => {
    const byKey: Record<string, string> = {};
    for (const room of rooms) {
      for (const a of room.amenities) {
        const key = amenityKey(a);
        if (key && !(key in byKey)) byKey[key] = getAmenityDisplayText(a);
      }
    }
    return byKey;
  }, [rooms, amenityKey, getAmenityDisplayText]);

  /** Amenity filter options, in the visitor's language, deduped and sorted. */
  const amenityOptions = useMemo(() => {
    // Keyed by the raw amenity, labelled in the visitor's language. The
    // selection used to BE the translated label, so switching language
    // left every selected value matching nothing — the results emptied
    // and the stale option vanished from the list, so it could not even
    // be unticked.
    // NB: `Map` in this file is lucide's map icon, not the global — hence a
    // plain record rather than the obvious `new Map()`.
    const byKey: Record<string, string> = {};
    for (const room of displayRooms) {
      for (const a of room.amenities) {
        const key = amenityKey(a);
        if (key && !(key in byKey)) byKey[key] = getAmenityDisplayText(a);
      }
    }
    return Object.entries(byKey)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [displayRooms, getAmenityDisplayText]);

  const hasActiveFilters =
    selectedCity !== "all" ||
    Boolean(priceRange.min) ||
    Boolean(priceRange.max) ||
    Boolean(moveInDate) ||
    availableOnly ||
    selectedAmenities.length > 0;

  /** "14 m² · Bett 140 cm · Schreibtisch" — the card's one detail line. */
  const roomDetailLine = useCallback(
    (room: PropertyRoom) =>
      room.amenities.slice(0, 3).map(getAmenityDisplayText).join(" · "),
    [getAmenityDisplayText]
  );

  /**
   * "Verfügbar ab 01.11." when a move-out is CONFIRMED, "Ab sofort verfügbar" when the
   * room is free today, and "Warteliste" otherwise — a room whose only date is
   * the sitting contract's earliest possible end is not something a visitor
   * can act on, so it is never shown as a date.
   */
  const roomAvailabilityLabel = useCallback(
    (room: PropertyRoom) => {
      const de = language === "de";
      if (room.available) return de ? "Ab sofort verfügbar" : "Available now";
      if (room.nextAvailableDate) {
        // Local midnight, not UTC: see parseDateOnly.
        const d = parseDateOnly(room.nextAvailableDate);
        if (!Number.isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          return de
            ? `Verfügbar ab ${day}.${month}.`
            : `Available from ${day}.${month}.`;
        }
      }
      return de ? "Warteliste" : "Waiting list";
    },
    [language]
  );

  // Fallback translations (for when Storyblok → translation_cache is empty)
  const fallbackAmenitiesTranslations: Record<
    string,
    Record<string, string>
  > = {
    en: {
      "property.amenity.kitchen": "Shared Kitchen",
      "property.amenity.bathroom": "Shared Bathroom",
      "property.amenity.wc": "Shared WC",
      "property.amenity.laundry": "Shared Laundry",
    },
    de: {
      "property.amenity.kitchen": "Gemeinschaftsküche",
      "property.amenity.bathroom": "Gemeinschaftsbad",
      "property.amenity.wc": "Gemeinschafts-WC",
      "property.amenity.laundry": "Gemeinschaftliche Waschküche",
    },
  };

  const handleClearFilters = useCallback(() => {
    setSelectedCity("all");
    setPriceRange({ min: "", max: "" });
    setMoveInDate("");
    setAvailableOnly(false);
    navigate("/properties", { replace: true });
  }, [navigate]);

  // Track filter changes (skip initial render)
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    posthog.capture("property filters applied", {
      city: selectedCity,
      price_min: priceRange.min || null,
      price_max: priceRange.max || null,
      move_in_date: moveInDate || null,
    });
  }, [selectedCity, priceRange.min, priceRange.max, moveInDate]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ background: SAND }}>
        <div
          className="mx-auto max-w-[1328px] px-5 sm:px-8 lg:px-14"
          style={{ paddingTop: "calc(var(--hero-offset) + 2.5rem)" }}
        >
          <div
            className="h-11 w-[320px] animate-pulse rounded-lg"
            style={{ background: LINE }}
          />
          <div
            className="mt-7 h-[78px] w-full animate-pulse rounded-full"
            style={{ background: LINE }}
          />
          <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-10 pb-24 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-4">
                <div
                  className="h-[240px] animate-pulse rounded-[14px] sm:h-[300px]"
                  style={{ background: LINE }}
                />
                <div className="flex flex-col gap-2">
                  <div
                    className="h-3 w-20 animate-pulse rounded"
                    style={{ background: LINE }}
                  />
                  <div
                    className="h-5 w-3/4 animate-pulse rounded"
                    style={{ background: LINE }}
                  />
                  <div
                    className="h-4 w-1/2 animate-pulse rounded"
                    style={{ background: LINE }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">
            {t(
              "public.properties.errorLoadingProperties",
              "Error loading properties"
            )}
          </p>
          <p className="text-muted-foreground text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: SAND }}>
      {/* ===== TITLE + FILTER BAR ===== */}
      <section
        className="px-5 pb-10 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 2.5rem)" }}
      >
        <div className="mx-auto flex max-w-[1328px] flex-col gap-7">
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="max-w-[900px] font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
            style={{ letterSpacing: "-0.04em", color: INK }}
          >
            {/* The draft's own headline, not the `properties.title` string:
                that key still holds the previous marketing copy ("Find Your
                Perfect Room") and DB-backed i18n would win over a fallback. */}
            {language === "de"
              ? "Finde dein WG-Zimmer."
              : "Find your shared room."}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            <SearchFilterBar
              de={language === "de"}
              cities={cities}
              cityCounts={cityCounts}
              selectedCity={selectedCity}
              onCityChange={handleCityChange}
              moveInDate={moveInDate}
              onMoveInDate={setMoveInDate}
              priceRange={priceRange}
              onPriceRange={setPriceRange}
              priceBounds={priceBounds}
              amenityOptions={amenityOptions}
              amenityLabels={amenityLabels}
              selectedAmenities={selectedAmenities}
              onAmenities={setSelectedAmenities}
              onSubmit={() =>
                resultsRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
            />
          </motion.div>
        </div>
      </section>

      {/* ===== RESULTS ===== */}
      <section ref={resultsRef} className="px-5 pb-24 sm:px-8 lg:px-14">
        <div className="mx-auto flex max-w-[1328px] flex-col gap-7">
          <ResultsHeader
            de={language === "de"}
            count={filteredRooms.length}
            availableOnly={availableOnly}
            onAvailableOnly={setAvailableOnly}
            sortLabel=""
            sortControl={
              <Select
                value={sortBy}
                onValueChange={(v) => setSortBy(v as typeof sortBy)}
              >
                <SelectTrigger
                  className="h-auto w-auto gap-1.5 border-0 bg-transparent p-0 text-[15px] shadow-none focus:ring-0"
                  style={{ color: MUTED }}
                >
                  <span className="whitespace-nowrap">
                    {language === "de" ? "Sortiert nach:" : "Sorted by:"}
                  </span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-[14px]">
                  <SelectItem value="recommended">
                    {t(
                      "properties.sort.recommended",
                      language === "de" ? "Empfohlen" : "Recommended"
                    )}
                  </SelectItem>
                  <SelectItem value="price-asc">
                    {t(
                      "properties.sort.priceAsc",
                      language === "de"
                        ? "Preis aufsteigend"
                        : "Price: low to high"
                    )}
                  </SelectItem>
                  <SelectItem value="price-desc">
                    {t(
                      "properties.sort.priceDesc",
                      language === "de"
                        ? "Preis absteigend"
                        : "Price: high to low"
                    )}
                  </SelectItem>
                  <SelectItem value="newest">
                    {t(
                      "properties.sort.newest",
                      language === "de" ? "Neueste" : "Newest"
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            }
            viewMode={viewMode}
            onViewMode={setViewMode}
          />

          <AnimatePresence mode="wait">
            {filteredRooms.length === 0 && (
              <WaitlistBand
                de={language === "de"}
                city={selectedCity === "all" ? null : selectedCity}
                hasFilters={hasActiveFilters}
                onClear={handleClearFilters}
              />
            )}

            {viewMode === "grid" && filteredRooms.length > 0 && (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="flex flex-col gap-7"
              >
                <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleRooms.map((room: PropertyRoom, i: number) => (
                    <RoomResultCard
                      key={room.id}
                      room={room}
                      index={i}
                      detail={roomDetailLine(room)}
                      availability={roomAvailabilityLabel(room)}
                      isFavorited={favoritedRoomIds.has(room.roomId)}
                      de={language === "de"}
                      onFavoriteClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavoriteMutation.mutate({
                          propertyId: room.propertyId,
                          roomId: room.roomId,
                          isFavorited: favoritedRoomIds.has(room.roomId),
                        });
                      }}
                    />
                  ))}
                </div>
                {visibleCount < filteredRooms.length && (
                  <div className="flex justify-center pt-4">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      className="inline-flex h-12 items-center gap-2 rounded-full border px-7 text-[15px] font-semibold transition-transform duration-300 hover:-translate-y-0.5"
                      style={{
                        borderColor: LINE,
                        color: INK,
                        background: "#FFFFFF",
                      }}
                    >
                      {t(
                        "properties.loadMore",
                        language === "de" ? "Mehr laden" : "Load more"
                      )}
                      <span style={{ color: MUTED_SOFT }}>
                        ({filteredRooms.length - visibleCount})
                      </span>
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {viewMode === "map" && filteredRooms.length > 0 && (
              <motion.div
                key="map"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="overflow-hidden rounded-[14px] border"
                style={{ borderColor: LINE }}
              >
                <PropertyMapView
                  properties={filteredRooms.map((room) => ({
                    id: room.roomId,
                    title: room.title,
                    city: room.city,
                    price: room.price,
                    image: room.image,
                    available: room.available,
                    lat: room.lat,
                    lng: room.lng,
                    address: room.address,
                    roommates: 2,
                  }))}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <TenantFaq de={language === "de"} />

      {/* ===== NOTIFY ME ===== */}
      <NotifyBand de={language === "de"} />

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </div>
  );
};

export default Properties;
